import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ClientSelect } from '@nile/mangifera/src/Components/Ticket';
import { AccountClient } from '@nile/graphql';
import { isEmpty, omit } from 'lodash';
import SiteSelect from '@nile/projects/src/Components/Inputs/SiteSelect';
import {
  useSiteListForDropdown,
  useProduct,
  COMMON_CONSTANTS,
  getTranslatedText,
  reactSelectInlineError
} from '@nile/components';
import { Tooltip } from 'react-clone-tippy';
import { useIntl } from 'react-intl';

// Styled component with media queries for wrap behavior
const ClientSiteSelectionContainer = styled.div`
  display: flex;
  align-items: center;

  @media (max-width: 1600px) {
    flex-wrap: wrap;
  }
`;

const ToolTipWrapper = (props) => {
  const { title, children } = props;

  if (title) {
    return (
      <Tooltip
        title={title}
        position="bottom-start"
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
        arrow
        distance={10}
      >
        {children}
      </Tooltip>
    );
  } else {
    return <>{children}</>;
  }
};

function ClientSiteSelection(props) {
  const {
    client,
    site,
    updateProperties,
    hideClient = false,
    hideIcon = true,
    hideLabel = true,
    hideSite = false,
    clientsToFilter,
    disableAutoPopulateSite = false,
    disableClientEdit,
    disableSiteEdit,
    menuStyleProps,
    menuListStyleProps,
    noMargin = false,
    enableMultiSelectSites = false,
    disableClearable = false
  } = props;
  const intl = useIntl();
  const tooltipTitle = getTranslatedText(
    intl,
    'common.client.edit.disabled.tooltip.message'
  );
  const [selectedClient, setSelectedClient] = useState(client);
  const [selectedSite, setSelectedSite] = useState(site);
  const isITProduct = useProduct(COMMON_CONSTANTS.IT);
  const {
    getClientSites,
    data: sites,
    extraVariables
  } = useSiteListForDropdown();

  const getClientSite = (val) => {
    getClientSites(val.accountId, val);
  };
  useEffect(() => {
    if (sites) {
      if (!disableAutoPopulateSite) {
        const isSingleSite = sites.length === 1;
        const defaultSite = isSingleSite
          ? sites[0]
          : sites.find((el) => el.hq) || null;
        const selectedSiteValue = enableMultiSelectSites
          ? isSingleSite
            ? [sites[0]]
            : []
          : defaultSite;
        setSelectedSite(selectedSiteValue);
        updateProperties({
          properties: {
            client: extraVariables,
            site: selectedSiteValue
          }
        });
      } else {
        updateProperties({
          properties: {
            client: extraVariables,
            site: enableMultiSelectSites ? [] : null
          }
        });
      }
    }
  }, [sites]);
  useEffect(() => {
    if (selectedClient && !selectedSite) {
      getClientSite(selectedClient);
    }
  }, []);
  useEffect(() => {
    setSelectedClient(client || null);
    if (client && !site) {
      getClientSite(client);
    }
  }, [client?.accountId]);
  useEffect(() => {
    setSelectedSite(enableMultiSelectSites ? site || [] : site || null);
  }, [site, enableMultiSelectSites]);

  return (
    <ClientSiteSelectionContainer className="client-site-selection">
      {hideClient
        ? null
        : !isITProduct && (
            <div
              className={`w300 mb-14 ${
                disableClientEdit ? 'cursor-not-allowed' : ''
              }`}
            >
              <ToolTipWrapper title={disableClientEdit && tooltipTitle}>
                <ClientSelect
                  noMargin={noMargin}
                  fetchBefore
                  updateField={(_, val) => {
                    setSelectedClient(val);
                    setSelectedSite(null);
                    getClientSite(val);
                  }}
                  property="accountId"
                  // value="client"
                  selectedValue={selectedClient}
                  // value={selectedClient}
                  clientsToFilter={clientsToFilter}
                  value={selectedClient}
                  hideIcon={hideIcon}
                  showLabel={!hideLabel}
                  icon
                  disabled={disableClientEdit}
                  menuStyleProps={menuStyleProps}
                  menuListStyleProps={menuListStyleProps}
                  query={props?.clientQuery}
                />
              </ToolTipWrapper>
              <div className="w300">
                {!isEmpty(props?.errors) &&
                  !selectedClient?.accountId &&
                  reactSelectInlineError(
                    getTranslatedText(intl, 'common.validation.required', {
                      labelName: getTranslatedText(intl, 'common.client')
                    })
                  )}
              </div>
            </div>
          )}
      {!hideSite && (
        <div className="w300 site-select">
          <SiteSelect
            noMargin={noMargin}
            accountId={selectedClient?.accountId}
            updateSite={(val) => {
              setSelectedSite(val);
              updateProperties({
                properties: {
                  client: selectedClient,
                  site: enableMultiSelectSites
                    ? val.map((el) => omit(el, ['label', 'value']))
                    : omit(val, ['label', 'value'])
                }
              });
            }}
            ignoreAutoPopulateFirstValue
            site={selectedSite}
            hideIcon={hideIcon}
            hideLabel={hideLabel}
            icon
            isITProduct={isITProduct}
            disabled={disableSiteEdit}
            menuStyleProps={menuStyleProps}
            menuListStyleProps={menuListStyleProps}
            multiSelect={enableMultiSelectSites}
            disableClearable={disableClearable}
          />
          <div className="w300">
            {!isEmpty(props?.errors) &&
              selectedClient?.accountId &&
              selectedSite?.length === 0 &&
              reactSelectInlineError(
                getTranslatedText(intl, 'common.validation.required', {
                  labelName: getTranslatedText(intl, 'common.sites')
                })
              )}
          </div>
        </div>
      )}
    </ClientSiteSelectionContainer>
  );
}

export default ClientSiteSelection;
